// Contenido Teórico Especializado - Comprensión Lectora EXANI-II (Ceneval 2026)
// Elaborado con estándar pedagógico docente especialista en reactivos de admisión.

export const COMPRENSION_LECTORA_THEORY = {
  cl_201: {
    title: "Identificación de la Idea Principal e Ideas Secundarias",
    intro: "La idea principal es la columna vertebral de cualquier texto evaluado por Ceneval. Los reactivos miden tu capacidad para distinguirla de ejemplos, datos estadísticos y anécdotas accesorias.",
    sections: [
      {
        subtitle: "1. ¿Qué es y qué NO es la Idea Principal?",
        content: "- Es: La afirmación más abarcadora e indispensable. Si se elimina, el texto pierde su sentido y coherencia fundamental.\n- NO es: Un ejemplo ilustrativo, una fecha, una cita textual entrecomillada o una cifra estadística puntual (estos son siempre ideas secundarias)."
      },
      {
        subtitle: "2. Ubicación de la Idea Principal en el Párrafo",
        content: "- Textos Analizantes (Deductivos): La idea principal se ubica al principio (oración temática inicial) y luego se desglosan los argumentos y pruebas.\n- Textos Sintetizantes (Inductivos): Inicia con datos particulares y concluye con la idea principal a modo de conclusión.\n- Textos Encuadrados: La idea principal se plantea al inicio y se reformula al final con otras palabras."
      },
      {
        subtitle: "3. Método de Supresión para Validar",
        content: "Para comprobar si una oración es la idea principal, aplícale el Test de Supresión: lee el párrafo omitiendo mentalmente esa oración. Si el resto del texto queda inconexo y sin rumbo, has encontrado la idea principal."
      }
    ],
    examples: [
      {
        problem: "¿Cuál es la idea principal del siguiente fragmento? 'A pesar del avance de energías limpias, la dependencia global del petróleo sigue dictando las fluctuaciones geopolíticas. En 2024, el 80% del transporte operó con derivados fósiles y las tensiones en Oriente Medio elevaron costos.'",
        solution: "Opción correcta: 'La persistente dependencia energética del petróleo continúa condicionando el escenario geopolítico mundial'. (Es la afirmación rectora y global, los demás son datos de apoyo)."
      }
    ],
    exam_tips: "Ceneval redacta distractores que son 100% verdaderos según el texto, pero que son solo detalles secundarios. Para la idea principal, busca la opción que englobe a todo el texto, no a una sola línea."
  },

  cl_202: {
    title: "Estructura Textual: Introducción, Tesis, Desarrollo y Conclusión",
    intro: "Evalúa cómo se articulan las partes de un discurso escrito en los ámbitos de estudio, literario y de participación social.",
    sections: [
      {
        subtitle: "1. La Arquitectura Textual Clásica",
        content: "1. Introducción: Plantea el tema, contextualiza al lector y suele presentar la tesis (postura que se defenderá).\n2. Cuerpo o Desarrollo: Contiene los argumentos, refutaciones, evidencias empíricas y contraargumentos.\n3. Conclusión: Cierre donde se sintetiza la tesis demostrada, se proyectan consecuencias o se emite un llamado a la acción."
      },
      {
        subtitle: "2. Marcadores del Discurso que Revelan la Estructura",
        content: "- De inicio: En primer lugar, Para comenzar, Con respecto a...\n- De transición o contraste: Por el contrario, No obstante, En cambio...\n- De consecuencia: Por ende, En consecuencia, De ahí que...\n- De cierre: En suma, En conclusión, Para finalizar, En síntesis..."
      }
    ],
    examples: [
      {
        problem: "¿Qué función cumple el párrafo que inicia con 'En definitiva, los hallazgos demuestran que la reforestación urbana reduce el efecto isla de calor'?",
        solution: "Cumple la función de recapitulación o conclusión sintética, ya que el conector 'En definitiva' introduce el cierre demostrativo de los argumentos previos."
      }
    ],
    exam_tips: "Atiende siempre a los conectores al inicio de cada párrafo; son los semáforos que Ceneval utiliza para indicar la función estructural de cada fragmento."
  },

  cl_203: {
    title: "Propósito Comunicativo, Tono y Postura del Autor",
    intro: "Mide si eres capaz de identificar la intención pragmática del emisor: ¿pretende informar objetivamente, convencer mediante persuasión, satirizar o advertir?",
    sections: [
      {
        subtitle: "1. Propósitos Comunicativos Típicos",
        content: "- Informar / Divulgar: Lenguaje denotativo, objetivo, en tercera persona, sin adjetivos valorativos.\n- Persuadir / Convencer: Busca modificar la opinión o conducta del lector usando argumentos y apelaciones directas.\n- Criticar / Denunciar: Señala deficiencias, contradicciones o fallas institucionales o teóricas.\n- Prescribir / Normar: Da órdenes o pautas de procedimiento (manuales, reglamentos)."
      },
      {
        subtitle: "2. El Tono del Emisor",
        content: "El tono se deduce por el vocabulario y los adjetivos:\n- Objetivo/Neutro: Científico, aséptico.\n- Irónico/Sarcástico: Dice lo contrario para burlarse sutilmente.\n- Pesimista/Alarmista: Enfatiza catástrofes y riesgos inevitables.\n- Elogioso/Laudatorio: Exalta virtudes y logros."
      }
    ],
    examples: [
      {
        problem: "Un artículo describe las consecuencias del sedentarismo con frases como '¿esperaremos a que nuestro corazón colapse para dar el primer paso?'. ¿Cuál es el propósito del autor?",
        solution: "El uso de preguntas retóricas directas y llamados a la acción evidencia un propósito persuasivo o de concientización, no meramente informativo."
      }
    ],
    exam_tips: "Distingue siempre entre un texto informativo (que solo expone hechos) y uno argumentativo (donde el autor toma partido por una postura y la defiende)."
  },

  cl_204: {
    title: "Comprensión de Textos Científicos y de Divulgación Académica",
    intro: "El ámbito de estudio es uno de los tres pilares del EXANI-II. Te enfrentarás a textos de biología, astronomía, medicina o sociología con terminología especializada.",
    sections: [
      {
        subtitle: "1. Características del Texto Científico",
        content: "- Precisión léxica y ausencia de ambigüedad (univocidad).\n- Uso frecuente de datos cuantitativos, gráficos y tablas.\n- Enfoque metodológico: hipótesis, experimentación y conclusiones."
      },
      {
        subtitle: "2. Estrategia de Lectura Eficaz",
        content: "1. Lee primero el título y las preguntas del reactivo antes de leer el texto largo.\n2. Identifica el fenómeno en estudio y las variables que se relacionan.\n3. No te bloquees con términos técnicos complejos; su significado casi siempre se deduce por el contexto."
      }
    ],
    examples: [
      {
        problem: "Si un texto indica que 'la microbiota intestinal modula la respuesta inmune adaptativa', ¿qué se infiere?",
        solution: "Se infiere que existe una correlación biológica directa entre la composición bacteriana del sistema digestivo y la eficacia de las defensas del organismo."
      }
    ],
    exam_tips: "Ceneval no te pedirá que conozcas la ciencia de memoria, sino que localices la relación de causa-efecto explícita o implícita en la lectura."
  },

  cl_205: {
    title: "Textos Argumentativos y Ensayísticos (Tesis y Premisas)",
    intro: "Los ensayos y columnas de opinión evalúan la capacidad de distinguir entre hechos comprobables y opiniones subjetivas, así como la solidez de los argumentos.",
    sections: [
      {
        subtitle: "1. Anatomía del Argumento",
        content: "- Tesis: La afirmación central o postura del autor (ej. 'La inteligencia artificial debe regularse éticamente').\n- Premisas: Razones lógicas o evidencias que respaldan la tesis.\n- Contraargumentos: Objeciones que el autor anticipa y refuta para dar solidez a su texto."
      },
      {
        subtitle: "2. Tipos de Argumentos en el Examen",
        content: "- De Autoridad: Respaldado en un experto o institución reconocida.\n- De Datos/Estadísticas: Basado en números y estudios empíricos.\n- De Causa-Efecto: Demuestra que un evento es consecuencia directa de otro.\n- Por Analogía: Compara dos situaciones similares para inferir una conclusión."
      }
    ],
    examples: [
      {
        problem: "¿Cuál es la premisa que sostiene la tesis de que la jornada laboral debe reducirse en un texto que cita a países nórdicos?",
        solution: "La premisa es la evidencia empírica de que la reducción horaria previene el estrés y disminuye el ausentismo laboral sin afectar el volumen productivo."
      }
    ],
    exam_tips: "Cuidado con las falacias de generalización apresurada ('todos los jóvenes...') que Ceneval suele incluir como opciones incorrectas."
  },

  cl_206: {
    title: "Textos Periodísticos y Críticos (Noticia, Editorial y Columna)",
    intro: "Pertenecen al ámbito de participación social en el EXANI-II. Miden la lectura crítica de la prensa y medios de comunicación masiva.",
    sections: [
      {
        subtitle: "1. Géneros Periodísticos Informativos vs Opinión",
        content: "- Noticia: Relato objetivo de un hecho reciente. Responde a las 6 W (¿Qué?, ¿Quién?, ¿Cuándo?, ¿Dónde?, ¿Por qué?, ¿Cómo?).\n- Editorial: Postura institucional del periódico o medio (no va firmado por una sola persona).\n- Columna de Opinión: Análisis subjetivo y argumentado con la firma de un autor específico."
      }
    ],
    examples: [
      {
        problem: "¿Qué diferencia a una editorial de una columna en un periódico?",
        solution: "La editorial refleja la postura corporativa del medio y no lleva firma personal, mientras que la columna es la visión particular del analista que la suscribe."
      }
    ],
    exam_tips: "En los reactivos sobre editoriales, la pregunta suele ser: '¿Qué postura asume el diario frente a la reforma planteada?'."
  },

  cl_207: {
    title: "Comprensión de Textos Literarios (Narrativa, Personajes y Conflicto)",
    intro: "El ámbito literario abarca cuentos, novelas, microficción y poemas. Evalúa la comprensión de motivaciones psicológicas, atmósfera y conflicto dramático.",
    sections: [
      {
        subtitle: "1. Elementos de la Narrativa",
        content: "- Conflicto Central: La fuerza antagónica o problema que desestabiliza al protagonista.\n- Voz Narrativa: Primera persona (narrador protagonista/testigo) u omnisciente (tercera persona que conoce pensamientos y destinos).\n- Espacio y Tiempo: Cronológico, retrospectivo (flashback) o anticipatorio."
      }
    ],
    examples: [
      {
        problem: "En un relato donde el protagonista regresa a su pueblo natal tras 20 años y lo encuentra cubierto de ceniza, ¿cuál es el conflicto interno sugerido?",
        solution: "El choque entre la memoria idealizada de su juventud y la desolación irrevocable del paso del tiempo y la pérdida."
      }
    ],
    exam_tips: "Ceneval valora mucho la empatía lectora: ¿por qué actuó el personaje de esa manera? Revisa siempre sus diálogos y pensamientos."
  },

  cl_208: {
    title: "Inferencia, Sentido Figurado y Lenguaje Connotativo",
    intro: "La inferencia es la deducción lógica de información no dicha explícitamente en el texto. Es la habilidad más valorada en la sección predictiva.",
    sections: [
      {
        subtitle: "1. Sentido Denotativo vs Connotativo",
        content: "- Denotativo: El significado literal del diccionario (ej. 'El corazón bombea sangre').\n- Connotativo (Figurado): Significado simbólico o metafórico (ej. 'Tenía un corazón de piedra' -> persona insensible)."
      },
      {
        subtitle: "2. Cómo Realizar una Inferencia Válida",
        content: "Una inferencia válida se apoya en evidencias del texto:\nPistas del texto + Conocimiento lógico del mundo = Inferencia válida\nNunca elijas una opción que contradiga al texto o que exagere más allá de lo razonable."
      }
    ],
    examples: [
      {
        problem: "'El capitán miró el barómetro que caía en picada, ordenó asegurar las escotillas y mandó a toda la tripulación a sus puestos.' ¿Qué se infiere?",
        solution: "Se infiere que se aproxima una fuerte tormenta inminente (caída de presión en el barómetro), aunque la palabra 'tormenta' no aparezca explícitamente."
      }
    ],
    exam_tips: "Si una opción repite literalmente una frase del texto, casi nunca es una inferencia; es solo información explícita."
  },

  cl_209: {
    title: "Síntesis, Conclusiones y Evaluación Crítica del Texto",
    intro: "Cierre del área de comprensión lectora. Evalúa tu capacidad para resumir textos extensos en una sola frase y determinar la validez de los argumentos presentados.",
    sections: [
      {
        subtitle: "1. El Resumen Ejecutivo o Síntesis",
        content: "Un resumen perfecto en Ceneval debe cumplir con tres criterios:\n1. Contener la idea principal y los argumentos centrales.\n2. Omitir detalles accesorios y ejemplos.\n3. No agregar opiniones personales ni juicios que el autor original no hizo."
      }
    ],
    examples: [
      {
        problem: "¿Cuál es el mejor título que sintetiza un artículo sobre la extinción de las abejas y su impacto en la agricultura?",
        solution: "'Crisis de los polinizadores y su amenaza a la seguridad alimentaria global'."
      }
    ],
    exam_tips: "Para elegir el mejor título de una lectura, descarta los títulos demasiado poéticos o demasiado específicos; escoge el que resuma el tema y la perspectiva del autor."
  }
};

